import StakeApiRequest from "@/common/lib/stakeApiRequest";
import ApiRequest from "@/common/lib/apiRequest";
import vaults from "@/common/config/vaults";
import projects from "@/common/config/projects";

const sortProjects = projects => {
  return [
    projects[0],
    ...projects
      .slice(1)
      .sort((a, b) => a.staking?.token?.localeCompare(b.staking?.token)),
  ];
};

const mutationTypes = {
  UPDATE_VAULTS_CONFIG: "UPDATE_VAULTS_CONFIG",
  SET_CURRENT_PROJECT: "SET_CURRENT_PROJECT",
  SET_PROJECTS: "SET_PROJECTS",
};

const state = {
  vaultGroups: vaults,
  projects: sortProjects(projects),
  latestProject: null,
};

const actions = {
  updateVaultGroups({ commit }, data) {
    commit(mutationTypes.UPDATE_VAULTS_CONFIG, data);
  },
  setCurrentProject({ commit, dispatch }, project) {
    commit(mutationTypes.SET_CURRENT_PROJECT, project);
    if (project && project.implemented) {
      dispatch(
        "setPageMeta",
        {
          title: project.text,
          description: `Delegate, stake, and earn rewards on your ${project.staking.token.toUpperCase()} with Unagii.`,
          keywords: `Unagii / Stake / Yield / Digital assets / Crypto / ${project.text}`,
        },
        { root: true }
      );
    }
  },
  async fetchNetworkConfigs({ commit, state, dispatch }) {
    let configs = [];
    let chainIds = {};
    try {
      [configs, chainIds] = await Promise.all([
        ApiRequest.get("/network_configurations"),
        StakeApiRequest.get("/latest_chain_ids"),
      ]);
    } catch (error) {}
    const projectsWithConfig = state.projects.map(project => {
      if (project.name === 'kyber') {
        return {
          ...project,
          status: {
            status: "live",
            displayStatus: "Live",
            active: true,
          },
        };
      }
      let defaultCfg = {
        status: "offline",
        displayStatus: "Offline",
        active: false,
      };
      let cfg = configs.find(
        cf => cf.name === project.projectName || cf.name === project.name
      );
      if (!cfg) {
        cfg = defaultCfg;
        project.implemented = false;
      } else {
        project.implemented = true;
      }
      cfg.active = cfg.status === "live";
      if (chainIds[project.name]) {
        project.staking.chainId = chainIds[project.name];
      }
      return {
        ...project,
        status: cfg,
      };
    });
    if (state.latestProject) {
      const currentProject = projectsWithConfig.find(
        pr => pr.name === state.latestProject.name
      );
      currentProject && dispatch("setCurrentProject", currentProject);
    }
    commit(mutationTypes.SET_PROJECTS, projectsWithConfig);
  },
};

const mutations = {
  [mutationTypes.UPDATE_VAULTS_CONFIG](state, data) {
    state.vaultGroups = data;
  },
  [mutationTypes.SET_CURRENT_PROJECT](state, project) {
    state.latestProject = project;
  },
  [mutationTypes.SET_PROJECTS](state, projects) {
    state.projects = projects;
  },
};

const getters = {
  vaultGroups: state => state.vaultGroups,
  allVaults: state =>
    state.vaultGroups
      .map(item =>
        item.tokens.map(tokenItem => ({
          ...tokenItem,
          vaultText: item.text,
          vaultType: item.name,
          isLegacy: item.isLegacy,
          vault: `${item.name}-${tokenItem.coin.toUpperCase()}`,
        }))
      )
      .flat(),
  vaultCoinNames: state => {
    const tokens = state.vaultGroups
      .filter(item => item.active)
      .reduce((acc, cur) => [...acc, ...cur.tokens.map(item => item.coin)], []);
    return Array.from(new Set(tokens));
  },
  getVaultByTypeAndToken: state => ({ type, token }) => {
    const vaultGroup = state.vaultGroups.find(item => item.name === type);
    if (!vaultGroup) return;
    const vault = vaultGroup.tokens.find(
      item => item.coin.toLowerCase() === token.toLowerCase()
    );
    if (!vault) return;
    return {
      ...vault,
      vaultType: type,
      vaultText: vaultGroup.text,
      vault: `${vaultGroup.name}-${vault.coin.toUpperCase()}`,
    };
  },
  getVaultByIdentity: state => identity => {
    identity = identity.toLowerCase();
    const [coin, version] = identity.split("-");
    return state.vaultGroups
      .map(item =>
        item.tokens.map(tokenItem => ({
          ...tokenItem,
          vaultText: item.text,
          vaultType: item.name,
          vault: `${item.name}-${tokenItem.coin.toUpperCase()}`,
        }))
      )
      .flat()
      .find(
        item =>
          item.coin === coin &&
          (item.identity.toLowerCase() === identity ||
            (!version && item.isLatest))
      );
  },
  latestProject: state => state.latestProject,
  projects: state => state.projects,
  projectByNetworkMap: state =>
    state.projects.reduce(
      (acc, cur) => ({
        [cur.config.network]: cur,
        ...acc,
      }),
      {}
    ),
  getProjectByName: state => name =>
    state.projects.find(pr => pr.name === name || pr.projectName === name),
  tendermintStakingProjects: state =>
    state.projects.filter(
      item =>
        item.implemented &&
        item.status?.active &&
        item.ecosystem === "Tendermint"
    ),
  ethereumStakingProjects: state =>
    state.projects.filter(
      item =>
        item.implemented && item.status?.active && item.ecosystem === "Ethereum"
    ),
  solanaStakingProjects: state =>
    state.projects.filter(
      item =>
        item.implemented && item.status?.active && item.ecosystem === "Solana"
    ),
  suiStakingProjects: state =>
    state.projects.filter(
      item =>
        item.implemented && item.status?.active && item.ecosystem === "Sui"
    ),
  activeProjects: state =>
    state.projects.filter(item => item.implemented && item.status?.active),
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
